import React, { useState } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import { Box, Button, CircularProgress, Typography } from '@mui/material';

import { authTokenAtom } from 'atoms/authAtoms';
import { stageConfigAtom } from 'atoms/Rtool/stageConfigAtoms';
import { powertrainAtom } from 'atoms/powertrainAtom';
import { gearIdAtom } from 'atoms/gearAtom';
import { errorAtom } from 'atoms/error';
import { mappingAtom } from 'atoms/mappingAtoms';
import { useSnackbar } from 'utils/hooks/useSnackbar';
import { toUserMessage } from 'utils/apiErrorMessage';
import { buildCalcConditionResult, type CalcConditionResult } from 'utils/rCalcConditionResult';
import { submitComponentTargetCalculation, submitComponentTargetCalculationHev } from 'api/submitRtool';
import { CalcConditionResultAlerts } from './CalcConditionResultAlerts';

export const ComponentTargetCalculation: React.FC = () => {
    const authToken = useAtomValue(authTokenAtom);
    const config = useAtomValue(stageConfigAtom);
    const powertrain = useAtomValue(powertrainAtom);
    const gearId = useAtomValue(gearIdAtom);
    const setError = useSetAtom(errorAtom);
    const mapping = useAtomValue(mappingAtom);
    const { showError, showWarning } = useSnackbar();

    // HEV は gear 単位で一括計算する
    const isHev = powertrain === 'hev';

    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState<CalcConditionResult | null>(null);

    const handleClick = async () => {
        if (isHev && !gearId) {
            showWarning("HEV では Gear を選択してください。");
            return;
        }
        setLoading(true);
        setResult(null);
        try {
            // calc（HEV は gear）単位の一括呼び出し。BE が候補 condition を列挙し 4 分類サマリを返す。
            const summary = isHev
                ? await submitComponentTargetCalculationHev(authToken, config, gearId!)
                : await submitComponentTargetCalculation(authToken, config);
            setResult(buildCalcConditionResult(summary, mapping));
        } catch (e) {
            setError(toUserMessage(e));
            showError(toUserMessage(e));
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box>
            <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1 }}>
                コンポーネント目標値の計算
            </Typography>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                指定された条件に基づきコンポーネントごとの目標値を計算します。
            </Typography>

            <Button
                variant="contained"
                sx={{ minWidth: 140 }}
                disabled={loading || (isHev && !gearId)}
                startIcon={loading ? <CircularProgress size={18} /> : undefined}
                onClick={handleClick}
            >
                {loading ? '計算中...' : '計算実行'}
            </Button>

            <CalcConditionResultAlerts result={result} />
        </Box>
    );
};
